/**
 * The course document, schema version 2 (`07` CRS-013, §3.8: CRS-059 to CRS-066; CRS-090).
 * The one enforced shape, shared by the pipeline, the server, the engine and the views.
 *
 * Every object is strict: a key the spec does not name is refused. That is how the schema refuses a
 * document that carries an address, a size or a hash of a fine green grid (CRS-090): the only thing
 * of the fine grid a published document may hold is `green_height_map.stdev_m`.
 */
import { z } from 'zod';

const finite = z.number().finite();
const int = z.number().int();
const sha256 = z.string().regex(/^[0-9a-f]{64}$/, 'a sha256 in lower-case hex');

export const latLngSchema = z.strictObject({
  lat: finite.min(-90).max(90),
  lng: finite.min(-180).max(180),
});

/** A GeoJSON position: [longitude, latitude]. */
const position = z.tuple([finite.min(-180).max(180), finite.min(-90).max(90)]);

const linearRing = z
  .array(position)
  .min(4)
  .refine(
    (r) => r[0]![0] === r[r.length - 1]![0] && r[0]![1] === r[r.length - 1]![1],
    'a ring is closed: its last position equals its first',
  );

export const polygonSchema = z.strictObject({
  type: z.literal('Polygon'),
  coordinates: z.array(linearRing).min(1),
});

export const lineStringSchema = z.strictObject({
  type: z.literal('LineString'),
  coordinates: z.array(position).min(2),
});

/** A height grid (CRS-061). Heights are 16-bit whole numbers of `height_step_m` above `z0_m`, in the file at `url`. */
export const gridSchema = z.strictObject({
  cell_m: finite.positive(),
  cols: int.positive(),
  rows: int.positive(),
  x0: finite,
  y0: finite,
  z0_m: finite,
  height_step_m: finite.positive(),
  url: z.string().min(1),
  sha256,
});

export const LAND_COVER_CLASSES = [
  'rough_grass',
  'heather',
  'sand',
  'scrub',
  'woodland_floor',
  'rock',
  'marsh',
  'farmland',
  'built',
] as const;

/** A class grid (CRS-063): one index into `classes` per cell, in the file at `url`. */
export const classGridSchema = z.strictObject({
  cell_m: finite.positive(),
  cols: int.positive(),
  rows: int.positive(),
  x0: finite,
  y0: finite,
  classes: z.array(z.enum(LAND_COVER_CLASSES)).min(1),
  url: z.string().min(1),
  sha256,
});

export const FEATURE_TYPES = [
  'fairway',
  'first_cut',
  'rough',
  'deep_rough',
  'bunker',
  'water',
  'ob',
  'trees',
  'green',
  'fringe',
  'tee',
] as const;

export const WATER_KINDS = ['pond', 'lake', 'river', 'burn', 'ditch', 'sea'] as const;
export const BUNKER_KINDS = ['fairway', 'greenside', 'waste'] as const;
export const SPECIES_LOOKS = ['broadleaf', 'birch', 'pine', 'conifer', 'palm', 'bush'] as const;

const featureProperties = z.strictObject({
  firmness_hint: finite.optional(),
  hazard_class: z.string().optional(),
  water_kind: z.enum(WATER_KINDS).optional(),
  surface_level_m: finite.optional(),
  water_id: z.string().min(1).max(32).optional(),
  seasonal: z.boolean().optional(),
  wet_months: z.array(int.min(1).max(12)).optional(),
  bunker_kind: z.enum(BUNKER_KINDS).optional(),
  depth_m: finite.nonnegative().optional(),
  lip_height_m: finite.nonnegative().optional(),
  canopy_height_m: finite.positive().optional(),
  density: finite.min(0).max(1).optional(),
});

export const featureSchema = z
  .strictObject({
    type: z.enum(FEATURE_TYPES),
    geometry: polygonSchema,
    properties: featureProperties,
  })
  .superRefine((f, ctx) => {
    const p = f.properties;
    if (f.type === 'water') {
      // CRS-060: every water feature has a kind, a surface level, an id and a seasonal flag.
      for (const k of ['water_kind', 'surface_level_m', 'water_id', 'seasonal'] as const) {
        if (p[k] === undefined)
          ctx.addIssue({ code: 'custom', message: `water needs ${k} (CRS-060)` });
      }
      if (p.seasonal === true && !(p.wet_months && p.wet_months.length > 0)) {
        ctx.addIssue({
          code: 'custom',
          message: 'a seasonal water has at least one wet month (CRS-060)',
        });
      }
    }
    if (f.type === 'bunker') {
      for (const k of ['bunker_kind', 'depth_m', 'lip_height_m'] as const) {
        if (p[k] === undefined)
          ctx.addIssue({ code: 'custom', message: `a bunker needs ${k} (CRS-060)` });
      }
    }
    if (f.type === 'trees') {
      for (const k of ['canopy_height_m', 'density'] as const) {
        if (p[k] === undefined)
          ctx.addIssue({ code: 'custom', message: `a trees polygon needs ${k} (CRS-062)` });
      }
    }
  });

export const holeFrameSchema = z.strictObject({
  origin: latLngSchema,
  rotation_deg: finite.min(0).lt(360),
  base_elevation_m: finite,
});

export const PIN_SLOTS = ['easy', 'medium', 'hard', 'major'] as const;

export const holeSchema = z.strictObject({
  hole_no: int.min(1).max(18),
  par: z.union([z.literal(3), z.literal(4), z.literal(5)]),
  stroke_index: int.min(1).max(18),
  exposure: finite.positive(),
  dogleg: z.enum(['none', 'left', 'right']),
  low_lying: z.boolean(),
  frame: holeFrameSchema,
  line_of_play: lineStringSchema,
  features: z.array(featureSchema).min(1),
  scenery: z.array(
    z.strictObject({
      type: z.enum(['path', 'bridge', 'wall']),
      geometry: lineStringSchema,
      width_m: finite.positive(),
    }),
  ),
  buildings: z.array(
    z.strictObject({
      footprint: polygonSchema,
      height_m: finite.positive(),
      kind: z.enum(['clubhouse', 'course_building', 'other']),
    }),
  ),
  elevation_grid: gridSchema,
  land_cover: classGridSchema,
  trees: z.strictObject({ count: int.nonnegative().max(1500), url: z.string().min(1), sha256 }),
  /** The coarse green picture (CRS-090): the only green heights that are published. */
  green_coarse: gridSchema,
  /** The one number of the fine green grid that is published (CRS-007, CRS-090). */
  green_height_map: z.strictObject({ stdev_m: finite.nonnegative() }),
  pin_positions: z
    .array(
      z.strictObject({ slot: z.enum(PIN_SLOTS), point: z.strictObject({ x: finite, y: finite }) }),
    )
    .length(4)
    .refine(
      (p) => new Set(p.map((q) => q.slot)).size === 4,
      'all four pin slots, once each (CRS-013)',
    ),
});

/** The eight styles of `07` §3.2 (CRS-005); codes of `core.course_styles` (`05`). */
export const COURSE_STYLES = [
  'links',
  'heathland',
  'parkland',
  'moorland',
  'clifftop',
  'desert',
  'mountain',
  'resort',
] as const;

export const courseSchema = z
  .strictObject({
    course_id: z.uuid(),
    club_id: z.uuid(),
    version: int.positive(),
    status: z.enum(['draft', 'approved', 'archived']),
    schema_version: z.literal(2),
    name: z.string().min(1),
    style: z.enum(COURSE_STYLES),
    is_flagship: z.boolean(),
    licence_type: z.enum(['procedural', 'partner_free', 'licensed_paid']),
    design_method: z.enum([
      'generated',
      'handcrafted',
      'ai_designer',
      'ready_made',
      'player_adjusted',
      'redesign',
    ]),
    hub: z.enum(['uki', 'usa']),
    nation: z.string().min(1),
    area: z.string().min(1),
    centroid: latLngSchema,
    altitude_m: finite,
    country_code: z.string().length(2),
    time_zone: z.string().min(1),
    climate_zone: z.string().min(1),
    soil_class: z.enum(['sand', 'loam', 'clay']),
    fairway_irrigation: z.boolean(),
    grass_types: z.strictObject({ fairway: z.string(), green: z.string() }).nullable(),
    exposure_factor: finite.min(0.6).max(1.2),
    boundary_polygon: polygonSchema,
    generator_seed: z.string().nullable(),
    pipeline_version: z.string().nullable(),
    attributions: z.array(z.string()),
    elevation_source: z.string().min(1),
    source_resolution_m: finite.positive(),
    backdrop: z.strictObject({
      origin: latLngSchema,
      elevation: gridSchema,
      land_cover: classGridSchema,
    }),
    holes: z.array(holeSchema).min(1).max(18),
    tee_sets: z
      .array(
        z.strictObject({
          name: z.string().min(1),
          colour: z.string().min(1),
          gender_rec: z.enum(['any', 'm', 'f']),
          holes: z
            .array(
              z.strictObject({
                hole_no: int.min(1).max(18),
                yards: finite.positive(),
                tee_point: latLngSchema,
              }),
            )
            .min(1),
          total_yards: finite.positive(),
          total_par: int.positive(),
        }),
      )
      .min(1),
    ratings: z.array(
      z.strictObject({
        tee_set: z.string().min(1),
        scratch_score: finite,
        bogey_score: finite,
        difficulty_factor: finite,
        status: z.enum(['provisional', 'settled']),
        rounds_counted: int.nonnegative(),
        last_recalculated_at: z.iso.datetime({ offset: true }),
      }),
    ),
  })
  .superRefine((c, ctx) => {
    // CRS-060: two features share a water_id only where they are the same water: same kind, same level.
    const water = new Map<string, { kind?: string; level?: number }>();
    for (const h of c.holes) {
      for (const f of h.features) {
        if (f.type !== 'water' || !f.properties.water_id) continue;
        const seen = water.get(f.properties.water_id);
        const now = { kind: f.properties.water_kind, level: f.properties.surface_level_m };
        if (seen && (seen.kind !== now.kind || seen.level !== now.level)) {
          ctx.addIssue({
            code: 'custom',
            message: `water_id ${f.properties.water_id} names two different waters (CRS-060)`,
          });
        }
        water.set(f.properties.water_id, now);
      }
    }
    const holeNos = c.holes.map((h) => h.hole_no);
    if (new Set(holeNos).size !== holeNos.length)
      ctx.addIssue({ code: 'custom', message: 'hole numbers are unique' });
  });

export type Course = z.infer<typeof courseSchema>;
export type Hole = z.infer<typeof holeSchema>;
export type Feature = z.infer<typeof featureSchema>;
export type Grid = z.infer<typeof gridSchema>;
export type ClassGrid = z.infer<typeof classGridSchema>;
